import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  api,
  type Category,
  type DocumentSummary,
  type Group,
} from '../api/client';
import { useSession } from '../session/SessionContext';
import { formateadorDeDia } from '../formato';
import { BarraDeFiltros, Buscador, LimpiarFiltros, Segmentado } from '../components/Filtros';
import { PermisosDocumento } from '../components/PermisosDocumento';
import { MarcaDeLectura } from '../components/MarcaDeLectura';
import { AvisoError, AvisoExito } from '../components/Aviso';
import { Paginacion } from '../components/Paginacion';
import { Modal } from '../components/Modal';
import { SelectorDeGrupos } from '../components/SelectorDeGrupos';
import { Palomita } from '../components/Iconos';
import { claveVisible } from '../documentos/clave';

const TONO_ESTADO: Record<string, string> = {
  PUBLISHED: 'bueno',
  EXPIRED: 'aviso',
  DRAFT: 'neutro',
  UNPUBLISHED: 'critico',
  ARCHIVED: 'neutro',
};

/** Filtros que viven en la URL. Se listan aquí para limpiarlos de una sola vez. */
const FILTROS = [
  'q',
  'categoryId',
  'groupId',
  'isPrintable',
  'isDownloadable',
  'mandatoryReading',
  'includeExpired',
] as const;

/** El orden no es un filtro: no cuenta para «quitar filtros» ni se borra con ellos. */
const ORDENES = ['relevance', 'recent', 'title', 'expiry'] as const;

export function Documentos({ grupos }: { grupos: Group[] }) {
  const { t, i18n } = useTranslation();
  const { estado, puede } = useSession();
  const navegar = useNavigate();
  // Lo que deja dicho otra pantalla al volver aquí: «se eliminó tal documento».
  const { state: estadoDeNavegacion } = useLocation() as { state: { mensaje?: string } | null };

  /**
   * Los filtros viven en la URL, no en el estado del componente: así una
   * búsqueda se puede compartir por correo, el botón «atrás» funciona y volver
   * del detalle no borra lo que la persona había filtrado.
   */
  const [parametros, setParametros] = useSearchParams();

  const misGrupos =
    estado.status === 'autenticado' ? estado.session.groups.map((grupo) => grupo.id) : [];
  const [documentos, setDocumentos] = useState<DocumentSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [porPagina, setPorPagina] = useState(25);
  const [categorias, setCategorias] = useState<Category[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [mensaje, setMensaje] = useState<string | null>(estadoDeNavegacion?.mensaje ?? null);
  const [ocupado, setOcupado] = useState(false);
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [cargando, setCargando] = useState(true);

  const fecha = formateadorDeDia(i18n.language);
  const consulta = parametros.toString();

  const buscado = parametros.get('q') ?? '';
  const [texto, setTexto] = useState(buscado);
  useEffect(() => setTexto(buscado), [buscado]);
  useEffect(() => {
    if (texto.trim() === buscado) return;
    // 350 ms: lo justo para que escribir «reglamento» sea una consulta y no diez.
    const esperar = window.setTimeout(() => fijar('q', texto.trim()), 350);
    return () => window.clearTimeout(esperar);
    // `fijar` se rehace en cada render; incluirlo dispararía la espera sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto, buscado]);
  const filtrosActivos = useMemo(
    () => FILTROS.filter((clave) => parametros.get(clave)).length,
    [parametros],
  );

  const fijar = useCallback(
    (clave: string, valor: string) => {
      const siguientes = new URLSearchParams(parametros);
      if (valor) siguientes.set(clave, valor);
      else siguientes.delete(clave);
      // Cambiar un filtro vuelve a la primera página: quedarse en la cuatro de
      // un resultado que ahora tiene una sola es una pantalla vacía sin motivo.
      siguientes.delete('page');
      setParametros(siguientes, { replace: true });
    },
    [parametros, setParametros],
  );

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    api
      .documents(consulta ? `?${consulta}` : '')
      .then((pagina) => {
        if (!vigente) return;
        setDocumentos(pagina.items);
        setTotal(pagina.totalItems);
        setPorPagina(pagina.pageSize);
        setError(null);
      })
      .catch((e) => vigente && setError(e))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [consulta]);

  useEffect(() => {
    api.categories().then(setCategorias).catch(setError);
  }, []);

  async function crear(
    evento: React.FormEvent<HTMLFormElement>,
    gruposElegidos: string[],
    permisosElegidos: { imprimir: boolean; descargar: boolean },
  ) {
    evento.preventDefault();
    const formulario = evento.currentTarget;
    const datos = new FormData(formulario);
    setOcupado(true);
    setError(null);
    try {
      const codigo = String(datos.get('referenceCode') ?? '').trim();
      const creado = await api.createDocument({
        title: String(datos.get('title')),
        ...(codigo ? { referenceCode: codigo } : {}),
        categoryId: String(datos.get('categoryId')),
        issuedOn: String(datos.get('issuedOn')),
        expiresOn: String(datos.get('expiresOn')) || undefined,
        groupIds: gruposElegidos,
        isPrintable: permisosElegidos.imprimir,
        isDownloadable: permisosElegidos.descargar,
        mandatoryReading: datos.get('mandatoryReading') === 'on',
        readingDeadlineDays:
          datos.get('mandatoryReading') === 'on'
            ? Number(datos.get('readingDeadlineDays'))
            : undefined,
      });
      formulario.reset();
      setNuevoAbierto(false);

      // Los grupos son el único mecanismo de visibilidad, así que es posible
      // crear un documento que uno mismo no ve. Decirlo es mejor que dejar que
      // la siguiente lectura devuelva un 404 sin explicación.
      // Quien tiene `document.view_all` lo verá de todas formas: avisarle de
      // que «no lo verá» sería falso, y la falsa alarma se aprende a ignorar.
      if (puede('document.view_all') || gruposElegidos.some((g) => misGrupos.includes(g))) {
        navegar(`/documentos/${creado.id}`);
      } else {
        setMensaje(t('documents.createdInvisible', { code: claveVisible(creado) }));
      }
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('documents.title')}</h1>
        <p className="pagina__bajada">{t('documents.subtitle')}</p>
      </div>

      <AvisoError error={error} />
      {mensaje && <AvisoExito>{mensaje}</AvisoExito>}

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('documents.catalog')}</h2>
          <span className="tarjeta__nota">
            {cargando ? t('common.loading') : t('documents.count', { count: total })}
          </span>
          {puede('document.upload') && (
            <button className="boton" type="button" onClick={() => setNuevoAbierto(true)}>
              {t('documents.newAction')}
            </button>
          )}
        </div>

        <BarraDeFiltros>
          {/* Busca en la ficha —código, título y palabras clave—, no dentro del
              PDF. Es lo que la caja necesita para encontrar un normativo por su
              nombre, y lo que se puede sostener sin indexar el contenido de
              documentos que no todo el mundo puede ver. */}
          <Buscador
            etiqueta={t('documents.searchPlaceholder')}
            valor={texto}
            onCambio={setTexto}
            id="q"
          />
          <div className="campo">
            <label htmlFor="categoryId">{t('documents.category')}</label>
            <select
              id="categoryId"
              value={parametros.get('categoryId') ?? ''}
              onChange={(e) => fijar('categoryId', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {'— '.repeat(categoria.depth)}
                  {categoria.name}
                </option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="groupId">{t('documents.group')}</label>
            <select
              id="groupId"
              value={parametros.get('groupId') ?? ''}
              onChange={(e) => fijar('groupId', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              {grupos.map((grupo) => (
                <option key={grupo.id} value={grupo.id}>
                  {grupo.name}
                </option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="isPrintable">{t('documents.printable')}</label>
            <select
              id="isPrintable"
              value={parametros.get('isPrintable') ?? ''}
              onChange={(e) => fijar('isPrintable', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              <option value="true">{t('documents.allowed')}</option>
              <option value="false">{t('documents.notAllowed')}</option>
            </select>
          </div>

          <div className="campo">
            <label htmlFor="isDownloadable">{t('documents.downloadable')}</label>
            <select
              id="isDownloadable"
              value={parametros.get('isDownloadable') ?? ''}
              onChange={(e) => fijar('isDownloadable', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              <option value="true">{t('documents.allowed')}</option>
              <option value="false">{t('documents.notAllowed')}</option>
            </select>
          </div>

          <div className="campo">
            <label htmlFor="mandatoryReading">{t('documents.mandatoryColumn')}</label>
            <select
              id="mandatoryReading"
              value={parametros.get('mandatoryReading') ?? ''}
              onChange={(e) => fijar('mandatoryReading', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              <option value="true">{t('documents.onlyMandatory')}</option>
              <option value="false">{t('documents.onlyOptional')}</option>
            </select>
          </div>

          <Segmentado
            etiqueta={t('documents.includeExpired')}
            valor={parametros.get('includeExpired') ?? ''}
            onCambio={(valor) => fijar('includeExpired', valor)}
            opciones={[
              { valor: '', etiqueta: t('documents.onlyCurrent') },
              { valor: 'true', etiqueta: t('documents.alsoExpired') },
            ]}
          />

          <div className="campo">
            <label htmlFor="sort">{t('documents.sortBy')}</label>
            <select
              id="sort"
              value={parametros.get('sort') ?? ''}
              onChange={(e) => fijar('sort', e.target.value)}
            >
              {ORDENES.map((orden) => (
                <option key={orden} value={orden === 'relevance' ? '' : orden}>
                  {t(`documents.sort.${orden}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="barra-filtros__acciones">
            <LimpiarFiltros
              hay={filtrosActivos > 0}
              onLimpiar={() => setParametros(new URLSearchParams(), { replace: true })}
            />
          </div>
        </BarraDeFiltros>

        {documentos.length === 0 ? (
          <div className="vacio">
            <span className="vacio__titulo">
              {filtrosActivos > 0 ? t('documents.noMatches') : t('documents.empty')}
            </span>
            <span>{filtrosActivos > 0 ? t('documents.noMatchesHint') : t('documents.emptyHint')}</span>
          </div>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                {/* Sin columna de clave: la pone el sistema, nadie la escribe y
                    nadie la cita en el día a día. El título es por lo que se
                    busca un normativo, así que abre la tabla. La clave sigue
                    existiendo —en la ficha, en el nombre del archivo descargado
                    y en la bitácora— y sigue encontrando documentos al
                    buscarla. */}
                <tr>
                  <th>{t('documents.name')}</th>
                  <th>{t('documents.category')}</th>
                  {/* Encabezado corto y con explicación al pasar el cursor: la
                      columna vale por lo compacta. */}
                  <th title={t('documents.permissionsColumnHelp')}>{t('documents.permissionsColumn')}</th>
                  <th title={t('documents.mandatoryColumnHelp')}>{t('documents.mandatoryColumn')}</th>
                  <th>{t('documents.version')}</th>
                  <th>{t('documents.validUntil')}</th>
                  <th>{t('users.status')}</th>
                </tr>
              </thead>
              <tbody>
                {documentos.map((documento) => (
                  <tr
                    key={documento.id}
                    className="fila--enlace"
                    tabIndex={0}
                    role="link"
                    onClick={() => navegar(`/documentos/${documento.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navegar(`/documentos/${documento.id}`);
                    }}
                  >
                    <td>
                      {documento.title}
                      {/* El código con el que la caja conoce el documento, si lo
                          tiene: es por lo que mucha gente lo busca. */}
                      {documento.referenceCode && (
                        <div className="campo__ayuda mono">{documento.referenceCode}</div>
                      )}
                    </td>
                    <td>{documento.categoryName}</td>
                    <td>
                      <PermisosDocumento
                        isPrintable={documento.isPrintable}
                        printWatermark={documento.printWatermark}
                        isDownloadable={documento.isDownloadable}
                        downloadWatermark={documento.downloadWatermark}
                      />
                    </td>
                    <td>
                      <MarcaDeLectura
                        obligatoria={documento.mandatoryReading}
                        plazoEnDias={documento.readingDeadlineDays}
                      />
                    </td>
                    <td className="mono">
                      {/* Un borrador todavía no tiene versión: la raya lo dice
                          mejor que un hueco. */}
                      {documento.versionLabel
                        ? t('documents.versionShort', { number: documento.versionLabel })
                        : '—'}
                    </td>
                    <td className="mono">
                      {documento.expiresOn ? fecha.format(new Date(documento.expiresOn)) : '—'}
                    </td>
                    <td>
                      <span className={`pildora pildora--${TONO_ESTADO[documento.status] ?? 'neutro'}`}>
                        <span className="pildora__punto" aria-hidden="true" />
                        {t(`document.status.${documento.status}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Paginacion total={total} porPagina={porPagina} cargando={cargando} />

      </section>

      {puede('document.upload') && nuevoAbierto && (
        <FormularioNuevo
          categorias={categorias}
          grupos={grupos}
          ocupado={ocupado}
          onEnviar={crear}
          onCerrar={() => setNuevoAbierto(false)}
        />
      )}
    </>
  );
}

/**
 * Alta de un documento, en ventana.
 *
 * Antes era una tarjeta al final de la página: se pulsaba «Nuevo documento»
 * arriba y el formulario aparecía debajo de la tabla entera, fuera de la
 * pantalla. Quien no se desplazaba concluía que el botón no hacía nada. La
 * ventana pone la tarea delante y encierra el foco dentro, que es justo lo que
 * pide un alta con nueve campos.
 */
function FormularioNuevo({
  categorias,
  grupos,
  ocupado,
  onEnviar,
  onCerrar,
}: {
  categorias: Category[];
  grupos: Group[];
  ocupado: boolean;
  onEnviar: (
    evento: React.FormEvent<HTMLFormElement>,
    gruposElegidos: string[],
    permisos: { imprimir: boolean; descargar: boolean },
  ) => void;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [obligatoria, setObligatoria] = useState(false);
  // Controladas, y no sólo leídas del formulario al enviar: la ficha se marca
  // con una clase además de con `:has()`, que algunos navegadores todavía en uso
  // en la caja no entienden, y para eso React tiene que saber si están puestas.
  const [imprimir, setImprimir] = useState(false);
  const [descargar, setDescargar] = useState(false);

  return (
    <Modal titulo={t('documents.newTitle')} onCerrar={onCerrar}>
      <p className="campo__ayuda" style={{ marginBottom: '.9rem' }}>
        {t('documents.newExplanation')}
      </p>
      <form
        className="formulario-alta"
        onSubmit={(evento) => onEnviar(evento, elegidos, { imprimir, descargar })}
      >
        <div className="campos">
          <div className="campo">
            <label htmlFor="title">{t('documents.name')}</label>
            <input id="title" name="title" required minLength={3} />
          </div>
          {/* Texto libre y opcional: el código impreso en el propio normativo
              («MP-CRE-04», «Circular 12/2026»). No es la clave del portal, que
              la sigue poniendo el sistema. */}
          <div className="campo">
            <label htmlFor="referenceCode">{t('documents.referenceCode')}</label>
            <input id="referenceCode" name="referenceCode" maxLength={100} autoComplete="off" />
            <span className="campo__ayuda">{t('documents.referenceCodeHelp')}</span>
          </div>
          <div className="campo">
            <label htmlFor="categoryIdNuevo">{t('documents.category')}</label>
            <select id="categoryIdNuevo" name="categoryId" required defaultValue="">
              <option value="" disabled>
                —
              </option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {'— '.repeat(categoria.depth)}
                  {categoria.name}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="issuedOn">{t('documents.issuedOn')}</label>
            <input id="issuedOn" name="issuedOn" type="date" required />
          </div>
          <div className="campo">
            <label htmlFor="expiresOn">{t('documents.validUntil')}</label>
            <input id="expiresOn" name="expiresOn" type="date" />
            <span className="campo__ayuda">{t('documents.validUntilHelp')}</span>
          </div>
        </div>

        <fieldset className="campo">
          <legend>{t('documents.groups')}</legend>
          <span className="campo__ayuda">{t('documents.groupsHelp')}</span>
          <SelectorDeGrupos
            grupos={grupos}
            elegidos={elegidos}
            onCambio={setElegidos}
            deshabilitado={ocupado}
            avisoVacio={{ texto: t('groups.noneChosenWarning'), grave: true }}
            incluyeRama
          />
        </fieldset>

        {/* Desde que descargar e imprimir dejaron de depender del rol, estos dos
            interruptores son el único control que hay sobre ello. Se ven como lo
            que son —una decisión, no una nota al pie— y con la misma ficha que
            los grupos, porque se deciden en el mismo momento. */}
        <fieldset className="campo">
          <legend>{t('documents.permissionsTitle')}</legend>
          <span className="campo__ayuda">{t('documents.permissionsHelp')}</span>
          <div className="grupos-selector__rejilla">
            <label className={`casilla-ficha${imprimir ? ' casilla-ficha--marcada' : ''}`}>
              <input
                type="checkbox"
                name="isPrintable"
                checked={imprimir}
                onChange={(e) => setImprimir(e.target.checked)}
              />
              <span className="casilla-ficha__marca" aria-hidden="true">
                <Palomita />
              </span>
              <span className="casilla-ficha__nombre">{t('documents.printable')}</span>
            </label>
            <label className={`casilla-ficha${descargar ? ' casilla-ficha--marcada' : ''}`}>
              <input
                type="checkbox"
                name="isDownloadable"
                checked={descargar}
                onChange={(e) => setDescargar(e.target.checked)}
              />
              <span className="casilla-ficha__marca" aria-hidden="true">
                <Palomita />
              </span>
              <span className="casilla-ficha__nombre">{t('documents.downloadable')}</span>
            </label>
          </div>
        </fieldset>

        {/* El plazo aparece **al marcar la casilla**, no antes. Un campo de días
            junto a una casilla apagada invita a llenarlo, y un plazo sin
            obligación no hace nada: el servidor lo rechaza y con razón. */}
        <fieldset className="campo">
          <legend>{t('documents.mandatoryTitle')}</legend>
          <span className="campo__ayuda">{t('documents.mandatoryHelp')}</span>
          <label className={`casilla-ficha${obligatoria ? ' casilla-ficha--marcada' : ''}`}>
            <input
              type="checkbox"
              name="mandatoryReading"
              checked={obligatoria}
              onChange={(e) => setObligatoria(e.target.checked)}
            />
            <span className="casilla-ficha__marca" aria-hidden="true">
              <Palomita />
            </span>
            <span className="casilla-ficha__nombre">{t('documents.mandatoryReading')}</span>
          </label>
          {obligatoria && (
            <div className="campo campo--anidado">
              <label htmlFor="readingDeadlineDays">{t('documents.deadlineDays')}</label>
              <input
                id="readingDeadlineDays"
                name="readingDeadlineDays"
                type="number"
                min={1}
                max={365}
                defaultValue={30}
                required
              />
              <span className="campo__ayuda">{t('documents.deadlineHelp')}</span>
            </div>
          )}
        </fieldset>

        <div className="tarjeta__acciones">
          {/* El servidor exige al menos un grupo. Impedirlo aquí ahorra el viaje
              y, sobre todo, el mensaje de validación que llega sin contexto. */}
          <button className="boton" type="submit" disabled={ocupado || elegidos.length === 0}>
            {t('documents.createAction')}
          </button>
          <button
            className="boton boton--fantasma"
            type="button"
            disabled={ocupado}
            onClick={onCerrar}
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
